import CodeSandboxOutlined from '@ant-design/icons/CodeSandboxOutlined'
import DeleteOutlined from '@ant-design/icons/DeleteOutlined'
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined'
import UnorderedListOutlined from '@ant-design/icons/UnorderedListOutlined'
import WarningTwoTone from '@ant-design/icons/WarningTwoTone'
import { Button, Tooltip } from 'antd'
import {
  convertKeyToLabel,
  ONTOLOGY_FIELD_INPUTS,
  OntologyField,
  OntologyFieldDefinition,
  OntologyFieldType,
  OntologyValueType,
} from 'features/dynamicOntology'
import isNil from 'lodash/isNil'
import isPlainObject from 'lodash/isPlainObject'
import omit from 'lodash/omit'
import React, {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { RolodexHistogramFilter } from 'types'

import { AddPropertyButton, getInitialValueForType } from './AddPropertyButton'
import { EditableLabel } from './EditableLabel'
import {
  inferPropertyType,
  PropertyType,
  PropertyTypeSelect,
} from './PropertyTypeSelect'

const INDENT_PER_LEVEL = 0.5

function resolveSetState<T>(action: SetStateAction<T>, prevValue: T): T {
  return typeof action === 'function'
    ? (action as (prevState: T) => T)(prevValue)
    : action
}

const getPrimitiveLeafValue = (
  value: OntologyValueType | undefined
): string | number | null | undefined => {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    value === null
  ) {
    return value
  }

  return undefined
}

export function getNextKey(
  prefix: string,
  object: Record<string, unknown>
): string {
  const keys = Object.keys(object ?? {})
  const maxSuffix = keys.reduce((max, key) => {
    if (!key.startsWith(prefix)) {
      return max
    }
    const numericSuffix = Number.parseInt(key.slice(prefix.length), 10)
    if (Number.isNaN(numericSuffix)) {
      return max
    }
    return Math.max(max, numericSuffix)
  }, 0)

  return `${prefix}${maxSuffix + 1}`
}

const getLabelKeyPrefix = (propertyKey?: string): string => {
  if (!propertyKey) {
    return ''
  }

  const lastDotIndex = propertyKey.lastIndexOf('.')

  return lastDotIndex >= 0 ? propertyKey.slice(0, lastDotIndex + 1) : ''
}

export const PropertyField = styled.div<{ $indent?: number }>`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-left: ${({ $indent = 0 }) => `${$indent}rem`};
  margin-bottom: 0.75rem;
  break-inside: avoid;
  width: ${({ $indent = 0 }) =>
    $indent > 0 ? `calc(100% - ${$indent}rem)` : '100%'};
`

export const PropertyLabel = styled.label`
  color: ${CantileverTheme.colors.gray['400']};
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin: 0;
  margin-left: 0.2rem;
`

export const PropertyValue = styled.div`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  background-color: ${({ theme }) => theme.forecast.cardGroupBackground};
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  min-height: 2.25rem;
  display: flex;
  align-items: center;
  word-break: break-word;
`

export const NestedContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.forecast.cardGroupBackground};
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const EntryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const AddButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
`

type ObjectSetter = Dispatch<SetStateAction<OntologyValueType | undefined>>

const createArraySetter = (
  setObject: ObjectSetter,
  index: number,
  currentValue?: OntologyValueType
): Dispatch<SetStateAction<OntologyValueType | undefined>> => {
  return (val) => {
    setObject((prevObject: OntologyValueType | undefined) => {
      const prevArray = Array.isArray(prevObject)
        ? (prevObject as ReadonlyArray<OntologyValueType>)
        : undefined
      const previousValue =
        prevArray && index < prevArray.length ? prevArray[index] : currentValue
      const nextValue = resolveSetState(
        val,
        previousValue as OntologyValueType | undefined
      )

      if (nextValue === undefined) {
        if (!prevArray || index >= prevArray.length) {
          return prevObject
        }
        const nextArray = prevArray.filter((_, i) => i !== index)
        return nextArray as unknown as OntologyValueType
      }

      const nextArray: Array<OntologyValueType> = prevArray
        ? [...prevArray]
        : []

      nextArray[index] = nextValue

      return nextArray as unknown as OntologyValueType
    })
  }
}

const createNestedRecordSetter = (
  setObject: ObjectSetter,
  key: string,
  currentValue?: OntologyValueType
): Dispatch<SetStateAction<OntologyValueType | undefined>> => {
  return (val) => {
    setObject((prevObject: OntologyValueType | undefined) => {
      const isRecord = isPlainObject(prevObject) && !Array.isArray(prevObject)
      const prevRecord: Record<string, OntologyValueType | undefined> = isRecord
        ? {
            ...(prevObject as Record<string, OntologyValueType | undefined>),
          }
        : {}
      const hasPrevKey = Object.prototype.hasOwnProperty.call(prevRecord, key)
      const previousValue = hasPrevKey ? prevRecord[key] : currentValue
      const nextValue = resolveSetState(
        val,
        previousValue as OntologyValueType | undefined
      )

      if (nextValue === undefined) {
        if (!hasPrevKey) {
          return prevObject
        }
        return omit(prevRecord, key)
      }

      return {
        ...prevRecord,
        [key]: nextValue,
      }
    })
  }
}

const createNestedLabelSetter = (
  setObject: ObjectSetter,
  key: string
): Dispatch<SetStateAction<string>> => {
  return (action) => {
    setObject((prevObject: OntologyValueType | undefined) => {
      if (!isPlainObject(prevObject) || Array.isArray(prevObject)) {
        return prevObject
      }

      const prevRecord = prevObject as Record<string, OntologyValueType>

      if (!Object.prototype.hasOwnProperty.call(prevRecord, key)) {
        return prevObject
      }

      const rawNextKey = resolveSetState(action, key)
      const nextKey = rawNextKey?.trim()

      if (!nextKey || nextKey === key) {
        return prevObject
      }

      if (Object.prototype.hasOwnProperty.call(prevRecord, nextKey)) {
        return prevObject
      }

      const entries = Object.entries(prevRecord)
      const nextEntries = entries.map(([entryKey, entryValue]) =>
        entryKey === key ? [nextKey, entryValue] : [entryKey, entryValue]
      ) as Array<[string, OntologyValueType | undefined]>

      const nextRecord = nextEntries.reduce<
        Record<string, OntologyValueType | undefined>
      >((acc, [entryKey, entryValue]) => {
        acc[entryKey] = entryValue
        return acc
      }, {})

      return nextRecord as unknown as OntologyValueType
    })
  }
}

const LeafContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const LeafActionsContainer = styled.div`
  display: flex;
  align-items: center;
`

const LEAF_ALLOWED_TYPES: ReadonlyArray<OntologyFieldType> = [
  'text',
  'number',
  'boolean',
  'enum',
  'date',
]

const COLOR_MISSING_VALUE = 'rgba(255, 165, 0, 0.15)'
const LeafFieldContainer = styled.div<{ $warning?: boolean }>`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  width: 100%;
  ${({ $warning }) =>
    $warning &&
    `
    background-color: ${COLOR_MISSING_VALUE};
    border-radius: 0.375rem;
    padding: 0.5rem;
  `}
`

const StyledWarningIcon = styled(WarningTwoTone)`
  font-size: 24px;
`

const WarningFieldIcon = React.memo(() => (
  <Tooltip title='Missing value'>
    <StyledWarningIcon twoToneColor={CantileverTheme.colors.DeepRed} />
  </Tooltip>
))
WarningFieldIcon.displayName = 'WarningFieldIcon'

const StyledInfoIcon = styled(InfoCircleOutlined)`
  color: ${CantileverTheme.colors.gray['400']};
`

const PropertyLeafView: FC<RecursivePropertyViewProps & { indent: number }> = ({
  label,
  propertyKey,
  setLabel,
  indent,
  object,
  setObject,
  field,
  fieldDef,
  ontologyFields,
  filters,
  leafActions,
}) => {
  const [overrideFieldType, setOverrideFieldType] = useState<
    PropertyType | undefined
  >(undefined)
  const inferredFieldType = useMemo<PropertyType>(
    () =>
      overrideFieldType ??
      fieldDef?.type ??
      inferPropertyType(object) ??
      'text',
    [overrideFieldType, fieldDef?.type, object]
  )

  const LeafComponent = useMemo(() => {
    return ONTOLOGY_FIELD_INPUTS[inferredFieldType]
  }, [inferredFieldType])

  const safeSetObject = useCallback(
    (value: OntologyValueType | undefined) => {
      setObject?.(value ?? '')
    },
    [setObject]
  )

  const safeSetOverrideFieldType = useCallback(
    (type: PropertyType) => {
      setOverrideFieldType((prev) => {
        if (prev === type) {
          return prev
        }
        safeSetObject?.(undefined)
        return type
      })
    },
    [safeSetObject, setOverrideFieldType]
  )

  const ontologyFieldDef = propertyKey
    ? ontologyFields?.[propertyKey]
    : undefined
  const primitiveLeafValue = useMemo(
    () => getPrimitiveLeafValue(object),
    [object]
  )
  const leafActionSetter = useMemo<
    Dispatch<SetStateAction<string | number | null>> | undefined
  >(() => {
    if (!setObject) {
      return undefined
    }

    return (valueOrUpdater) => {
      if (typeof valueOrUpdater === 'function') {
        setObject((prevValue) => {
          const updater = valueOrUpdater as (
            prevState: string | number | null
          ) => string | number | null
          const previousPrimitive =
            getPrimitiveLeafValue(prevValue) ?? primitiveLeafValue ?? null

          return updater(previousPrimitive)
        })
        return
      }

      setObject(valueOrUpdater)
    }
  }, [setObject, primitiveLeafValue])

  const labelKeyPrefix = useMemo(
    () => getLabelKeyPrefix(propertyKey),
    [propertyKey]
  )

  if (!LeafComponent) {
    return null
  }

  const warning = ontologyFieldDef && (isNil(object) || object === '')

  return (
    <LeafFieldContainer $warning={warning}>
      {warning && <WarningFieldIcon />}
      <PropertyField $indent={indent}>
        <PropertyLabel>
          <EditableLabel
            keyPrefix={labelKeyPrefix}
            label={label}
            propertyKey={propertyKey}
            setLabel={setLabel}
            disableEdit={Boolean(fieldDef)}
          />
          {fieldDef?.description && (
            <Tooltip title={fieldDef.description}>
              <StyledInfoIcon />
            </Tooltip>
          )}
        </PropertyLabel>
        <LeafContainer>
          <LeafComponent
            value={object}
            onChange={safeSetObject}
            field={field}
            fieldDef={fieldDef}
            filters={filters}
          />
          {leafActions && (
            <LeafActionsContainer>
              {leafActions({
                field: propertyKey ?? label ?? '',
                object: primitiveLeafValue as OntologyValueType,
                setObject: leafActionSetter as Dispatch<
                  SetStateAction<OntologyValueType>
                >,
                fieldDef,
              })}
            </LeafActionsContainer>
          )}
          {!fieldDef && setObject && (
            <PropertyTypeSelect
              selectedType={inferredFieldType}
              setSelectedType={safeSetOverrideFieldType}
              allowedTypes={LEAF_ALLOWED_TYPES}
            />
          )}
        </LeafContainer>
      </PropertyField>
    </LeafFieldContainer>
  )
}

export interface LeafActionsParams {
  field: string
  object: OntologyValueType
  setObject?: Dispatch<SetStateAction<OntologyValueType>>
  fieldDef?: OntologyFieldDefinition
}

export interface RecursivePropertyViewProps {
  keyPrefix?: string
  label: string | undefined
  propertyKey?: string
  object: OntologyValueType | undefined
  setObject?: Dispatch<SetStateAction<OntologyValueType | undefined>>
  setLabel?: Dispatch<SetStateAction<string>>
  indentLevel?: number
  field?: OntologyField
  fieldDef?: OntologyFieldDefinition
  ontologyFields?: Record<string, [string, OntologyFieldDefinition]>
  filters?: ReadonlyArray<RolodexHistogramFilter>
  leafActions?: (params: LeafActionsParams) => ReactNode
}

const RecursiveArrayPropertyView: FC<RecursivePropertyViewProps> = ({
  label,
  propertyKey,
  object,
  indentLevel = 0,
  setObject,
  setLabel,
  fieldDef,
  keyPrefix = '',
  field,
  ontologyFields,
  filters,
  leafActions,
  ...rest
}) => {
  const indent = indentLevel * INDENT_PER_LEVEL
  const values = object as ReadonlyArray<OntologyValueType>

  // Pre-compute keyPrefixes, setters, and delete handlers for each item
  const itemData = useMemo(
    () =>
      values.map((value, idx) => {
        const setter = setObject
          ? createArraySetter(setObject, idx, value as OntologyValueType)
          : undefined
        return {
          keyPrefix: keyPrefix + idx + '.',
          setter,
          deleteHandler: setter ? () => setter(undefined) : undefined,
        }
      }),
    [keyPrefix, values, setObject]
  )

  const handleAddArrayValue = useCallback(
    (type: PropertyType) => {
      if (!setObject) {
        return
      }

      setObject((prevObject: OntologyValueType | undefined) => {
        const baseArray = Array.isArray(prevObject)
          ? [...(prevObject as Array<OntologyValueType>)]
          : Array.isArray(object)
            ? [...(object as Array<OntologyValueType>)]
            : []

        baseArray.push(getInitialValueForType(type))

        return baseArray as unknown as OntologyValueType
      })
    },
    [object, setObject]
  )

  const labelKeyPrefix = useMemo(
    () => getLabelKeyPrefix(propertyKey),
    [propertyKey]
  )

  return (
    <PropertyField $indent={indent}>
      <PropertyLabel>
        <EditableLabel
          keyPrefix={labelKeyPrefix}
          label={label}
          propertyKey={propertyKey}
          setLabel={setLabel}
          disableEdit={Boolean(fieldDef)}
        />
      </PropertyLabel>
      <NestedContainer>
        {useMemo(
          () =>
            values.length > 0 ? (
              values.map((value, idx) => {
                const {
                  keyPrefix: itemKeyPrefix,
                  setter: nestedSetter,
                  deleteHandler,
                } = itemData[idx]

                return (
                  <EntryRow key={idx}>
                    {nestedSetter && !fieldDef && (
                      <Button
                        icon={<DeleteOutlined />}
                        type='text'
                        danger
                        size='small'
                        onClick={deleteHandler}
                      />
                    )}
                    <RecursivePropertyView
                      keyPrefix={itemKeyPrefix}
                      label={undefined}
                      object={value as OntologyValueType}
                      indentLevel={indentLevel + 1}
                      setObject={nestedSetter}
                      fieldDef={fieldDef}
                      field={field}
                      ontologyFields={ontologyFields}
                      filters={filters}
                      leafActions={leafActions}
                      {...rest}
                    />
                  </EntryRow>
                )
              })
            ) : (
              <EmptyListSplash />
            ),
          [
            field,
            fieldDef,
            filters,
            indentLevel,
            itemData,
            leafActions,
            ontologyFields,
            rest,
            values,
          ]
        )}
        {setObject && !fieldDef && (
          <AddButtonContainer>
            <AddPropertyButton onAddProperty={handleAddArrayValue} />
          </AddButtonContainer>
        )}
      </NestedContainer>
    </PropertyField>
  )
}

const RecursiveObjectPropertyView: FC<RecursivePropertyViewProps> = ({
  keyPrefix = '',
  label,
  propertyKey,
  object,
  indentLevel = 0,
  setObject,
  setLabel,
  ontologyFields,
  fieldDef,
  leafActions,
  ...rest
}) => {
  const indent = indentLevel * INDENT_PER_LEVEL
  const entries = useMemo(
    () => Object.entries(object as Record<string, unknown>),
    [object]
  )

  // Pre-compute metadata, setters, and delete handlers for each entry
  const entryData = useMemo(
    () =>
      entries.map(([key, value]) => {
        const [childField, childFieldDef] = ontologyFields?.[key] ?? [
          undefined,
          undefined,
        ]
        const nestedSetter = setObject
          ? createNestedRecordSetter(setObject, key, value as OntologyValueType)
          : undefined
        const labelSetter =
          setObject && nestedSetter && !childFieldDef
            ? createNestedLabelSetter(setObject, key)
            : undefined

        return {
          itemKeyPrefix: keyPrefix + key + '.',
          itemPropertyKey: keyPrefix + key,
          convertedLabel: childFieldDef?.name ?? convertKeyToLabel(key),
          childField,
          childFieldDef,
          nestedSetter,
          labelSetter,
          deleteHandler: nestedSetter
            ? () => nestedSetter(undefined)
            : undefined,
        }
      }),
    [entries, keyPrefix, ontologyFields, setObject]
  )

  const handleAddObjectProperty = useCallback(
    (type: PropertyType) => {
      if (!setObject) {
        return
      }

      setObject((prevObject: OntologyValueType | undefined) => {
        const baseRecord =
          isPlainObject(prevObject) && !Array.isArray(prevObject)
            ? (prevObject as Record<string, OntologyValueType>)
            : (object as Record<string, OntologyValueType>)

        const nextRecord: Record<string, OntologyValueType> = {
          ...baseRecord,
        }

        const propertyKey = getNextKey('field_', nextRecord)
        nextRecord[propertyKey] = getInitialValueForType(type)

        return nextRecord as unknown as OntologyValueType
      })
    },
    [object, setObject]
  )

  const labelKeyPrefix = useMemo(
    () => getLabelKeyPrefix(propertyKey),
    [propertyKey]
  )

  return (
    <PropertyField $indent={indent}>
      <PropertyLabel>
        <EditableLabel
          keyPrefix={labelKeyPrefix}
          label={label}
          propertyKey={propertyKey}
          setLabel={setLabel}
          disableEdit={Boolean(fieldDef)}
        />
      </PropertyLabel>
      <NestedContainer>
        {useMemo(
          () =>
            entries.length > 0 ? (
              entries.map(([key, value], idx) => {
                const {
                  itemKeyPrefix,
                  itemPropertyKey,
                  convertedLabel,
                  childField,
                  childFieldDef,
                  nestedSetter,
                  labelSetter,
                  deleteHandler,
                } = entryData[idx]

                return (
                  <EntryRow key={key}>
                    {nestedSetter && !childFieldDef && (
                      <Button
                        icon={<DeleteOutlined />}
                        type='text'
                        danger
                        size='small'
                        onClick={deleteHandler}
                        disabled={!nestedSetter}
                      />
                    )}
                    <RecursivePropertyView
                      keyPrefix={itemKeyPrefix}
                      label={convertedLabel}
                      propertyKey={itemPropertyKey}
                      object={value as OntologyValueType}
                      indentLevel={indentLevel + 1}
                      setObject={nestedSetter}
                      setLabel={labelSetter}
                      field={childField}
                      fieldDef={childFieldDef}
                      ontologyFields={ontologyFields}
                      leafActions={leafActions}
                      {...rest}
                    />
                  </EntryRow>
                )
              })
            ) : (
              <EmptyObjectSplash />
            ),
          [entries, entryData, indentLevel, leafActions, ontologyFields, rest]
        )}
        {setObject && !ontologyFields && (
          <AddButtonContainer>
            <AddPropertyButton onAddProperty={handleAddObjectProperty} />
          </AddButtonContainer>
        )}
      </NestedContainer>
    </PropertyField>
  )
}

/**
 * The meat of the object properties view.
 *
 * This component renders a property view for a given object, which can be an array, object, or 'leaf' (primitive value).
 * Recursions are handled by dispatching to a RecursiveArrayPropertyView or RecursiveObjectPropertyView.
 *
 * If you supply an 'ontologyFields' prop, the components will render according to the ontology, and will prevent
 * type- and key- edits at the root level.
 *
 * If you supply a 'setObject' prop, the component will render in 'Edit' mode.
 */
const RawRecursivePropertyView: FC<RecursivePropertyViewProps> = ({
  object,
  indentLevel = 0,
  ontologyFields,
  ...rest
}) => {
  const indent = indentLevel * INDENT_PER_LEVEL

  return Array.isArray(object) ? (
    <RecursiveArrayPropertyView
      object={object}
      indentLevel={indentLevel}
      ontologyFields={ontologyFields}
      {...rest}
    />
  ) : isPlainObject(object) ? (
    <RecursiveObjectPropertyView
      object={object}
      indentLevel={indentLevel}
      ontologyFields={ontologyFields}
      {...rest}
    />
  ) : (
    <PropertyLeafView
      object={object}
      indentLevel={indentLevel}
      ontologyFields={ontologyFields}
      indent={indent}
      {...rest}
    />
  )
}
export const RecursivePropertyView = React.memo(RawRecursivePropertyView)

const NoDataSplashContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  color: ${CantileverTheme.colors.gray['400']};
  text-align: center;
`

const StyledEmptyIcon = styled.div`
  color: gray;
  font-size: 1.5rem;
`

const RawEmptyListSplash = () => (
  <NoDataSplashContainer>
    <StyledEmptyIcon as={UnorderedListOutlined} />
    Empty List
  </NoDataSplashContainer>
)
const EmptyListSplash = React.memo(RawEmptyListSplash)
EmptyListSplash.displayName = 'EmptyListSplash'

const RawEmptyObjectSplash = () => (
  <NoDataSplashContainer>
    <StyledEmptyIcon as={CodeSandboxOutlined} />
    Empty Object
  </NoDataSplashContainer>
)
const EmptyObjectSplash = React.memo(RawEmptyObjectSplash)
EmptyObjectSplash.displayName = 'EmptyObjectSplash'
